import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, RotateCcw, Trash2, X } from "lucide-react";

export default function TrashModal({ onClose, onRestored }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trash");
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const restore = async (t) => {
    setBusy(t.id);
    try {
      const { data } = await api.post(`/trash/${t.id}/restore`);
      toast.success(`"${t.name}" geri getirildi (${data.restored} kayıt)`);
      await load();
      onRestored?.();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(null);
    }
  };

  const purge = async (t) => {
    if (!window.confirm(`"${t.name}" çöp kutusundan kalıcı olarak silinecek. Bu işlem geri alınamaz!`)) return;
    setBusy(t.id);
    try {
      await api.delete(`/trash/${t.id}`);
      toast.success("Kalıcı olarak silindi");
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="trash-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Trash2 size={18} /> Çöp Kutusu</h2>
            <p className="text-xs text-slate-500 mt-0.5">Silinen kaynaklar 30 gün saklanır, sonra otomatik olarak kalıcı silinir.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="trash-close-btn"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          {loading ? (
            <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-slate-500" data-testid="trash-empty">Çöp kutusu boş.</div>
          ) : (
            <div className="space-y-2" data-testid="trash-list">
              {items.map((t) => (
                <div key={t.id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3" data-testid={`trash-item-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    {t.publisher && <div className="text-[11px] font-bold text-indigo-600 uppercase truncate">{t.publisher}</div>}
                    <div className="font-semibold text-slate-900 truncate">{t.name}</div>
                    <div className="text-xs text-slate-500">
                      {t.sections} bölüm · {t.related} bağlı kayıt · Silindi: {new Date(t.deleted_at).toLocaleString("tr-TR")} · <span className="font-semibold text-amber-600">{t.days_left} gün kaldı</span>
                    </div>
                  </div>
                  <Button size="sm" disabled={busy === t.id} onClick={() => restore(t)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`trash-restore-${t.id}`}>
                    <RotateCcw size={13} /> <span className="ml-1">Geri Getir</span>
                  </Button>
                  <button disabled={busy === t.id} onClick={() => purge(t)} className="text-slate-300 hover:text-rose-600" title="Kalıcı Sil" data-testid={`trash-purge-${t.id}`}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
